import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  Visibility,
  ToggleOn,
  ToggleOff,
  Download
} from '@mui/icons-material';
import { superAdminService } from '../../../services/superAdmin/superAdminService';
import CompanyDetailDialog from './CompanyDetailDialog';
import { exportToExcel } from '../../../utils/excelExport';

const CompanyListPage = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['superAdminCompanies', search, paginationModel.page, paginationModel.pageSize],
    queryFn: async () => {
      const result = await superAdminService.getCompanies({
        search,
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize
      });
      if (!result.success) throw new Error(result.message);
      return result;
    }
  });

  const toggleMutation = useMutation({
    mutationFn: (id) => superAdminService.toggleCompanyActive(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['superAdminCompanies'] })
  });

  const handleExport = useCallback(() => {
    if (!data?.data) return;
    exportToExcel({
      title: 'Companies',
      fileName: 'EasyCheck_Companies.xlsx',
      sheetName: 'Companies',
      columns: [
        { header: 'Company Name', key: 'name', type: 'text', width: 30 },
        { header: 'Active', key: 'isActive', type: 'text' },
        { header: 'Users', key: 'userCount', type: 'number' },
        { header: 'Cheques', key: 'chequeCount', type: 'number' },
        { header: 'Created', key: 'createdDate', type: 'date' },
      ],
      data: data.data.map(c => ({
        ...c,
        isActive: c.isActive ? 'Yes' : 'No',
        createdDate: c.createdDate ? new Date(c.createdDate).toLocaleDateString('en-GB') : '',
      })),
    });
  }, [data]);

  const columns = [
    { field: 'name', headerName: 'Company Name', flex: 1, minWidth: 200 },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: ({ value }) => (
        <Chip
          label={value ? 'Active' : 'Inactive'}
          color={value ? 'success' : 'default'}
          size="small"
          sx={{ fontSize: '0.75rem' }}
        />
      )
    },
    {
      field: 'planId',
      headerName: 'Plan',
      width: 130,
      renderCell: ({ value }) => {
        const planLabels = { business: 'Business', businessPlus: 'Business Plus', premium: 'Premium' };
        const planColors = { business: 'default', businessPlus: 'primary', premium: 'warning' };
        return (
          <Chip
            label={planLabels[value] || 'Business'}
            color={planColors[value] || 'default'}
            size="small"
            sx={{ fontSize: '0.75rem' }}
          />
        );
      }
    },
    { field: 'userCount', headerName: 'Users', width: 80, type: 'number' },
    { field: 'chequeCount', headerName: 'Cheques', width: 100, type: 'number' },
    {
      field: 'createdDate',
      headerName: 'Signup Date',
      width: 130,
      valueFormatter: (params) => {
        const val = params?.value ?? params;
        return val ? new Date(val).toLocaleDateString() : '';
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => setSelectedCompanyId(row.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.isActive ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => toggleMutation.mutate(row.id)}
              color={row.isActive ? 'success' : 'default'}
            >
              {row.isActive ? <ToggleOn fontSize="small" /> : <ToggleOff fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Companies</Typography>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleExport}
          size="small"
          sx={{ borderColor: '#14b8a6', color: '#14b8a6', '&:hover': { borderColor: '#0d9488', backgroundColor: '#f0fdfa' } }}
        >
          Export Excel
        </Button>
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder="Search companies..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
          sx: { borderRadius: 2, backgroundColor: 'white' }
        }}
      />

      <Box sx={{ height: 'calc(100vh - 260px)', backgroundColor: 'white', borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <DataGrid
          rows={data?.data || []}
          columns={columns}
          loading={isLoading}
          rowCount={data?.totalCount || 0}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc' }
          }}
        />
      </Box>

      {selectedCompanyId && (
        <CompanyDetailDialog
          companyId={selectedCompanyId}
          open={!!selectedCompanyId}
          onClose={() => setSelectedCompanyId(null)}
        />
      )}
    </Box>
  );
};

export default CompanyListPage;
